import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';

/**
 * Hook to fetch subscription data and compute feature-gate flags.
 * canCustomize = plan IN (free,premium) AND status IN (active,trial,expiring_soon)
 * basic plan users see the upgrade card — customizations are ignored.
 */
export const useSubscription = (clinicId) => {
  const [subscription, setSubscription] = useState(null);
  const [plan, setPlan] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!clinicId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('clinic_subscriptions')
        .select(`
          *,
          subscription_status (
            id,
            name,
            description,
            color,
            is_active
          ),
          plans (
            id,
            name,
            description,
            price
          )
        `)
        .eq('clinic_id', clinicId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      setSubscription(data);
      setPlan(data?.plans || null);
      setStatus(data?.subscription_status || null);
    } catch (err) {
      if (import.meta.env.DEV) console.error('useSubscription error:', err);
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // free + premium plans with active subscription can customize
  // basic plan sees upgrade card (customizations ignored on public page)
  const canCustomize =
    !!plan &&
    !!status &&
    ['free', 'premium'].includes(plan.id) &&
    ['active', 'trial', 'expiring_soon'].includes(status.name);

  return { subscription, plan, status, loading, canCustomize, refetch: fetchSubscription };
};
